import React, { useEffect, useState, useRef } from 'react';
import {
  Edit,
  SimpleForm,
  useDataProvider,
  Toolbar,
  SaveButton,
  DeleteButton,
  useRecordContext,
} from 'react-admin';
import RecipesFormFields from '../customForms/RecipesFormFields';
import { Button, Box, Snackbar, Alert } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';


const ConfigsTitle = () => {
  const record = useRecordContext();
  return <span>Receita {record ? `${record.Name || 'sem nome'}` : ''}</span>;
};


const RecipesEdit = () => {
  const dataProvider = useDataProvider();
  const [fileOptions, setFileOptions] = useState({ Pdfs: [], Images: [], Videos: [] });
  const [companyID, setCompanyID] = useState(undefined);
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const fileInputRef = useRef();

  const fetchFiles = async () => {
    try {
      const result = await dataProvider.listFiles();
      const allFiles = [...(result.Pdfs || []), ...(result.Images || []), ...(result.Videos || [])];

      setFileOptions({
        Pdfs: allFiles.filter(f => f.toLowerCase().endsWith('.pdf')),
        Videos: allFiles.filter(f => f.toLowerCase().endsWith('.mp4')),
        Images: allFiles.filter(f => !f.toLowerCase().endsWith('.pdf') && !f.toLowerCase().endsWith('.mp4')),
      });

      setCompanyID(result.companyID);
    } catch (err) {
      console.error('Failed to fetch files', err);
      setErrorMessage('Erro ao carregar os ficheiros.');
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    try {
      setUploading(true);
      await dataProvider.uploadFiles(files);
      await fetchFiles();
    } catch (error) {
      const serverMessage = error?.response?.data?.error;
      const fallbackMessage = error?.message || 'Erro desconhecido';
      const message = `Erro ao enviar os ficheiros: ${serverMessage || fallbackMessage}`;
      setErrorMessage(message);
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = null;
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const CustomToolbar = props => (
    <Toolbar
      {...props}
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 100,
        backgroundColor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        gap: 2,
        px: 2,
        py: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <SaveButton />
        <Button
          variant="contained"
          color="secondary"
          startIcon={<UploadIcon />}
          onClick={handleUploadClick}
          disabled={uploading}
        >
          {uploading ? 'A enviar...' : 'Adicionar ficheiros'}
        </Button>
        <input
          type="file"
          multiple
          hidden
          ref={fileInputRef}
          onChange={handleFileChange}
        />
      </Box>

      <DeleteButton mutationMode="pessimistic" label="Apagar" />
    </Toolbar>
  );

  return (
    <>
      <Edit title={<ConfigsTitle />}>
        <SimpleForm toolbar={<CustomToolbar />}>
          <RecipesFormFields
            fileOptions={fileOptions}
            companyID={companyID}
          />
        </SimpleForm>
      </Edit>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setSnackbarOpen(false)} sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RecipesEdit;
